import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Stack,
  TextInput,
  toast,
} from 'component-library';
import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorProductSubscriptionConfiguration } from '@/api/rest/resources/types/IndicatorProduct';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useUploadInSituDataFile } from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import {
  FILE_UPLOAD_VALIDATION,
  FORM_FIELDS,
  InSituFileUploadFormFieldValues,
  useInSituFileUploadForm,
} from '@/pages/shared/uploads/pages/hooks/useFormControlsInSituFileUpload';

type InSituFileUploadDialogProps = DialogProps & {
  config: IndicatorProductSubscriptionConfiguration;
};

export const InSituFileUploadDialog = ({ config, open, onOpenChange }: InSituFileUploadDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog data-testid='in-situ-file-upload-dialog' open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-[40vw]'>
        <DialogHeader>
          <DialogTitle>{t('shared.insituDataUpload.fileUpload.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody className='overflow-y-auto'>
          <Stack spacing={4}>
            <ValidationErrorsDisplay errors={config.validation_errors} />
            <span className='typography-body1'>{t('shared.insituDataUpload.fileUpload.description')}</span>
            <InSituFileUploadFormContent config={config} onSuccess={() => onOpenChange?.(false)} />
          </Stack>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const InSituFileUploadFormContent = ({
  config,
  onSuccess,
}: {
  config: IndicatorProductSubscriptionConfiguration;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { submit, isSubmitting } = useUploadInSituDataFile();

  const form = useInSituFileUploadForm({
    defaultValues: {
      [FORM_FIELDS.INDICATOR_NAME]: config.indicator_name,
      [FORM_FIELDS.DESCRIPTION]: config.description || '',
      [FORM_FIELDS.FILE]: config.file_info?.at(0)?.visible_filename || null,
    },
  });

  const [file, indicatorName] = form.watch([FORM_FIELDS.FILE, FORM_FIELDS.INDICATOR_NAME]);

  const validateFile = (validatedFile: File): string | null => {
    const fileExtension = `.${validatedFile.name.split('.').pop()?.toLowerCase()}`;

    if (!(FILE_UPLOAD_VALIDATION.ACCEPTED_FILE_TYPES as unknown as string[]).includes(fileExtension)) {
      return t('shared.insituDataUpload.fileUpload.validation.invalidFileType', {
        acceptedTypes: FILE_UPLOAD_VALIDATION.ACCEPTED_FILE_TYPES.join(', '),
      });
    }

    const fileSizeMB = validatedFile.size / 1024 / 1024;
    if (fileSizeMB > FILE_UPLOAD_VALIDATION.MAX_FILE_SIZE_MB) {
      return t('shared.insituDataUpload.fileUpload.validation.fileTooLarge', {
        maxSize: FILE_UPLOAD_VALIDATION.MAX_FILE_SIZE_MB,
      });
    }

    return null;
  };

  const handleFormSubmit = (values: InSituFileUploadFormFieldValues) => {
    const fileToUpload = values[FORM_FIELDS.FILE];
    if (!fileToUpload || typeof fileToUpload === 'string') {
      return;
    }

    const fileValidation = validateFile(fileToUpload);

    if (fileValidation) {
      toast({
        type: 'error',
        title: t('shared.insituDataUpload.toasts.uploadError.title'),
        description: fileValidation,
      });
      return;
    }

    const data = {
      file: fileToUpload,
      configurationId: config.id,
      description: values[FORM_FIELDS.DESCRIPTION],
    };

    submit(
      data,
      () => {
        toast({
          type: 'success',
          title: t('shared.insituDataUpload.toasts.uploadSuccess.title'),
          description: t('shared.insituDataUpload.toasts.uploadSuccess.description'),
        });
        onSuccess();
      },
      () =>
        toast({
          type: 'error',
          title: t('shared.insituDataUpload.toasts.uploadError.title'),
          description: t('shared.insituDataUpload.toasts.uploadError.description'),
        }),
    );
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newFile = e?.target?.files?.[0];
    if (newFile) {
      form.setValue(FORM_FIELDS.FILE, newFile);
    }
  };

  const requiredLabel = t('global.ui.form.input.required');

  const disableSubmit = !file || typeof file === 'string' || !indicatorName;

  return (
    <Form {...form}>
      <form data-testid='in-situ-file-upload-form' onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Stack spacing={4}>
          <FormField
            control={form.control}
            name={FORM_FIELDS.INDICATOR_NAME}
            rules={{ required: requiredLabel }}
            render={({ field }) => (
              <FormItem>
                <Stack spacing={2} className='w-full'>
                  <FormLabel>{t('shared.insituDataUpload.fileUpload.fields.indicator')} *</FormLabel>
                  <FormControl className='flex-1'>
                    <TextInput data-testid='indicator-name-input' {...field} disabled />
                  </FormControl>
                </Stack>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={FORM_FIELDS.DESCRIPTION}
            render={({ field }) => (
              <FormItem>
                <Stack spacing={2} className='w-full'>
                  <FormLabel>{t('shared.insituDataUpload.fileUpload.fields.description')}</FormLabel>
                  <FormControl className='flex-1'>
                    <TextInput
                      data-testid='description-input'
                      {...field}
                      placeholder={t('shared.insituDataUpload.fileUpload.fields.description')}
                      inputProps={{ maxLength: 1000 }}
                    />
                  </FormControl>
                </Stack>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={FORM_FIELDS.FILE}
            rules={{ required: requiredLabel }}
            render={({ field: { value } }) => (
              <FormItem>
                <Stack spacing={2} className='w-full'>
                  <FormLabel>{t('shared.insituDataUpload.fileUpload.fields.file')} *</FormLabel>
                  <FormControl className='flex-1'>
                    <div className='flex items-center gap-3'>
                      <input
                        ref={fileInputRef}
                        type='file'
                        data-testid='file-input'
                        accept={FILE_UPLOAD_VALIDATION.ACCEPTED_FILE_TYPES.join(',')}
                        onChange={handleFileChange}
                        className='hidden'
                      />
                      <Button type='button' variant='outline' onClick={() => fileInputRef.current?.click()}>
                        <i className='ri-upload-cloud-2-line' />
                        {t('shared.insituDataUpload.fileUpload.fields.selectFile')}
                      </Button>

                      {value && (
                        <span className='typography-body3 text-neutral-black-60'>
                          {typeof value !== 'string' ? value.name : value}
                        </span>
                      )}
                    </div>
                  </FormControl>
                </Stack>
                <FormMessage />
              </FormItem>
            )}
          />
          <Stack direction='row' className='justify-end'>
            <Button
              type='submit'
              data-testid='submit-button'
              variant='contained'
              className='w-fit'
              disabled={disableSubmit}
              loading={isSubmitting}
            >
              {t('global.ui.buttons.submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Form>
  );
};

type ValidationErrorsDisplayProps = {
  errors: string[];
};

const ValidationErrorsDisplay = ({ errors }: ValidationErrorsDisplayProps) => {
  const { t } = useTranslation();

  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <Alert data-testid='validation-errors-display' type='error' className='w-full'>
      <Stack spacing={2} className='col-span-full flex max-h-[250px] w-full overflow-x-hidden overflow-y-auto'>
        <Stack direction='row' spacing={2} className='items-center w-full'>
          <Icon icon='ri-error-warning-line' />
          <span className='typography-body1Semibold'>
            {t('shared.insituDataUpload.fileUpload.validationErrorsTitle')}
          </span>
        </Stack>
        <ul className='list-inside list-disc space-y-1 ml-6 w-full'>
          {errors.map((error, index) => (
            <li key={index} className='typography-body2'>
              {error}
            </li>
          ))}
        </ul>
      </Stack>
    </Alert>
  );
};
